// @givmo/fundraisers-module — a campaign's pledge template card (FCM-WEB34; D-FCM-61's decision 3, D-FCM-53):
// the form as the organizer types it, the template it stores (`PUT /fundraisers/{id}/pledge-template`,
// GFU-BE64), the server's preview of one page's sentence, and the stored terms as its summary shows them.
// Every request shape is the generated client's.
import type {
  PledgeMeasureKind,
  PledgeOfferAmountRule,
  PledgeOfferConditionKind,
  PledgeOfferCreate,
  PledgeOfferTieRule,
  PledgeOfferTone,
  PledgeTemplateOut,
  PledgeTemplateWindow,
  PledgeTemplateWrite,
} from '@givmo/api-client';
import { deadlineDay, endOfDay } from './layers-decode';
import { centsOf, dollarsOf, firstPitch, unitsOf, type RatePolicy } from './season-setup-decode';

/**
 * "Who's being counted?" (D-FCM-53): each person who joins (the server's `beneath` copy, onto every page
 * made by joining) or someone named (its `organizer` copy, onto the campaign's own pages and games only).
 */
export type TemplateWho = 'joiner' | 'named';

/** The card's form, as typed. */
export interface TemplateForm {
  who: TemplateWho;
  subject: string;
  unit: string;
  most: string;
  starts: Date | undefined;
  ends: Date | undefined;
  rate: string;
  cap: string;
}

export const EMPTY_TEMPLATE_FORM: TemplateForm = {
  who: 'joiner',
  subject: '',
  unit: '',
  most: '',
  starts: undefined,
  ends: undefined,
  rate: '',
  cap: '',
};

/** The day an instant falls on in `zone` (the template's own), else in the reader's. */
const dayOfInstant = (iso: string | null | undefined, zone?: string | null): Date | undefined => {
  if (!iso) return undefined;
  const at = new Date(iso);
  if (!Number.isFinite(at.getTime())) return undefined;
  return zone ? deadlineDay(iso, zone) : new Date(at.getFullYear(), at.getMonth(), at.getDate());
};

/** A stored template as the form edits it. */
export function formOfTemplate(template: PledgeTemplateOut): TemplateForm {
  const { measure, offer } = template;
  const named = (measure.subject_name ?? '').trim() !== '';
  return {
    who: named ? 'named' : 'joiner',
    subject: named ? measure.subject_name! : '',
    unit: measure.unit_label,
    most: measure.declared_max_units != null ? String(measure.declared_max_units) : '',
    starts: dayOfInstant(measure.starts_at, measure.timezone),
    ends: dayOfInstant(measure.ends_at, measure.timezone),
    rate: dollarsOf(offer.suggested_rate_cents),
    cap: dollarsOf(offer.default_cap_cents),
  };
}

const RATE_POLICIES: readonly string[] = ['suggested', 'fixed', 'pledgor_choice'];

/**
 * Whether the card's form can edit a stored template without changing what it doesn't show: a count per
 * unit, with no total, condition, step or baseline, and a rate policy it can name. Any other (a yes/no, a
 * flat amount, a season total, a condition) is shown as stored, never rewritten as one the form can make.
 */
export function cardCanEdit(template: PledgeTemplateOut): boolean {
  const { measure, offer } = template;
  return (
    measure.kind === 'count' &&
    !measure.rollup &&
    offer.amount_rule === 'per_unit' &&
    (offer.condition_kind ?? 'none') === 'none' &&
    offer.milestone_step == null &&
    offer.baseline == null &&
    RATE_POLICIES.includes(offer.rate_policy)
  );
}

/** Who sets the rate: the stored template's own, or "You suggest" for a new one (the drawn form). */
export function cardRatePolicy(stored: PledgeTemplateOut | null): RatePolicy {
  const policy = stored?.offer.rate_policy;
  return policy === 'fixed' || policy === 'pledgor_choice' ? policy : 'suggested';
}

/**
 * Whether the form says enough to store: a count, the most, a cap, the name it counts, and a rate unless the
 * stored template's pledgors choose their own.
 */
export function templateFormReady(form: TemplateForm, policy: RatePolicy = 'suggested'): boolean {
  return (
    form.unit.trim() !== '' &&
    Number(form.most) > 0 &&
    (policy === 'pledgor_choice' || centsOf(form.rate) !== null) &&
    centsOf(form.cap) !== null &&
    (form.who === 'joiner' || form.subject.trim() !== '')
  );
}

const day = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/**
 * The template the card stores: a count each copy dates from the form's days (or its own, with none). A new
 * one takes the drawn form's terms; an edit starts from the template as stored, field by field (the write
 * takes no other key), and replaces only what the form shows, so its words ("Exactly what counts", how it
 * reads), live counting, who sets the rate, its tone and when pledging closes stay as they were.
 */
export function cardTemplateBody(
  form: TemplateForm,
  zone: string,
  stored: PledgeTemplateOut | null = null,
): PledgeTemplateWrite {
  const dated = form.starts !== undefined || form.ends !== undefined;
  const shown = {
    unit_label: form.unit.trim(),
    subject_name: form.who === 'named' ? form.subject.trim() : null,
    declared_max_units: Number(form.most) > 0 ? Number(form.most) : null,
    starts_at: form.starts ? firstPitch(day(form.starts), '00:00', zone) : null,
    ends_at: form.ends ? endOfDay(form.ends, zone) : null,
    timezone: dated ? zone : null,
  };
  if (stored) {
    // Only a template `cardCanEdit` passes is edited here, so it has no total to write back. Its retired
    // opening never goes back (GFU-BE151 refuses one: pledging opens at publish, D-GFU-57), and a named
    // subject is always the organizer's official one (the server's only affiliation).
    const { measure, offer } = stored;
    const policy = cardRatePolicy(stored);
    return {
      measure: {
        kind: measure.kind as PledgeMeasureKind,
        count_precision: measure.count_precision === 1 ? 1 : 0,
        stat_key: measure.stat_key ?? null,
        qualifying_rule: measure.qualifying_rule ?? null,
        target_count: measure.target_count ?? null,
        ends_at_target: measure.ends_at_target,
        live_mode: measure.live_mode,
        count_phrase: measure.count_phrase ?? null,
        ...shown,
        health_data: false,
      },
      offer: {
        amount_rule: offer.amount_rule as PledgeOfferAmountRule,
        milestone_step: offer.milestone_step ?? null,
        baseline: offer.baseline ?? null,
        condition_kind: offer.condition_kind as PledgeOfferConditionKind,
        condition_value: offer.condition_value ?? null,
        condition_value_upper: offer.condition_value_upper ?? null,
        tie_rule: offer.tie_rule as PledgeOfferTieRule,
        rate_policy: policy,
        suggested_rate_cents:
          policy === 'pledgor_choice' ? (offer.suggested_rate_cents ?? null) : centsOf(form.rate),
        rate_min_cents: offer.rate_min_cents ?? null,
        rate_max_cents: offer.rate_max_cents ?? null,
        default_cap_cents: centsOf(form.cap) ?? 0,
        tone: offer.tone as PledgeOfferTone,
        window: offer.window as PledgeTemplateWindow,
      },
    };
  }
  return {
    measure: {
      kind: 'count',
      count_precision: 0,
      ...shown,
      qualifying_rule: null,
      live_mode: false,
      health_data: false,
      count_phrase: null,
    },
    offer: {
      amount_rule: 'per_unit',
      rate_policy: 'suggested',
      suggested_rate_cents: centsOf(form.rate),
      default_cap_cents: centsOf(form.cap) ?? 0,
      tone: 'celebratory',
      window: 'default',
    },
  };
}

/**
 * The server's sentence for one page the template would go on: the named subject's, or, for a template
 * about each joiner, one page that exists (`person`); dated by the form, else like a day from tomorrow. An
 * edit previews the terms it would store, the stored words among them.
 */
export function cardPreviewBody(
  campaignId: string,
  form: TemplateForm,
  person: string,
  zone: string,
  now: Date = new Date(),
  stored: PledgeTemplateOut | null = null,
): PledgeOfferCreate {
  const write = cardTemplateBody(form, zone, stored);
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const starts = form.starts ?? tomorrow;
  const ends = form.ends ?? starts;
  return {
    fundraiser_id: campaignId,
    measure: {
      kind: 'count',
      unit_label: write.measure.unit_label,
      count_precision: write.measure.count_precision ?? 0,
      subject_name: person,
      subject_affiliation: 'official',
      qualifying_rule: write.measure.qualifying_rule ?? null,
      declared_max_units: write.measure.declared_max_units ?? null,
      starts_at: firstPitch(day(starts), form.starts ? '00:00' : '12:00', zone),
      ends_at: endOfDay(ends, zone),
      timezone: zone,
      live_mode: write.measure.live_mode ?? false,
      health_data: false,
      count_phrase: write.measure.count_phrase ?? null,
    },
    amount_rule: 'per_unit',
    rate_policy: write.offer.rate_policy ?? 'suggested',
    suggested_rate_cents: write.offer.suggested_rate_cents,
    default_cap_cents: write.offer.default_cap_cents,
    tone: write.offer.tone ?? 'celebratory',
  };
}

/**
 * A stored template's terms as one page reads them (the server's preview): its own subject, else the page of
 * `person`; dated like a day from tomorrow, since only an Official Source event's name changes the sentence.
 * Null for a template the preview can't word (anything the card can't edit) or with no page to name.
 */
export function storedPreviewBody(
  campaignId: string,
  template: PledgeTemplateOut,
  person: string | null,
  zone: string,
  now: Date = new Date(),
): PledgeOfferCreate | null {
  if (!cardCanEdit(template)) return null;
  const subject = (template.measure.subject_name ?? '').trim() || person;
  if (!subject) return null;
  return cardPreviewBody(campaignId, formOfTemplate(template), subject, zone, now, template);
}

/** A stored amount as the summary says it: "$1", "$1.05". Whole cents, never a float. */
export function centsShown(cents: number | null | undefined): string {
  if (cents == null || !Number.isSafeInteger(cents) || cents < 0) return '';
  const rest = cents % 100;
  return rest === 0
    ? `$${Math.floor(cents / 100)}`
    : `$${Math.floor(cents / 100)}.${String(rest).padStart(2, '0')}`;
}

/** The plural the template counts, for its reach line ("runs"). */
export function templateUnits(form: Pick<TemplateForm, 'unit'>, fallback: string): string {
  return unitsOf(form.unit) || fallback;
}
